import { Carousel, spacingHelper } from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CarouselTile, PropertyTileProps } from '../PropertyTile/PropertyTile';
import {
  ShowMoreTilesData,
  trackShowMoreTiles,
} from '../../../analytics/event-tracking/trackShowMoreTiles';

const spaceForShadow = '0.125rem';
const halfOfImageHeight = '64px';

const CarouselWrapper = styled.div`
  &,
  ul {
    padding-bottom: ${spaceForShadow};
    padding-top: ${spaceForShadow};
    &::before,
    &::after {
      padding-left: ${spaceForShadow};
    }
    /* 
    note: spaceForShadow is needed to avoid layout shifting hover, and this cannot be fixed with the overflow attribute as that affects the carousel functionality
    */

    li {
      position: relative;
    }
  }

  .leftScrollButton,
  .rightScrollButton {
    top: calc(${halfOfImageHeight} + ${spacingHelper('twoExtraSmall')});
    transform: translateY(-50%);
  }

  .leftScrollButton {
    left: -${spacingHelper('medium')};
  }

  .rightScrollButton {
    right: -${spacingHelper('medium')};
  }
`;

type PropertyCarouselProps = {
  properties: PropertyTileProps[];
  tracking?: ShowMoreTilesData | null;
  preloadCount?: number;
  className?: string;
};

export const PropertyCarousel: FunctionComponent<PropertyCarouselProps> = ({
  properties,
  tracking,
  preloadCount = 0,
  className,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  // Ensure tracking only fires on first scroll
  // Requires key in <PropertyCarousel> to reset the state of hasScrolled
  useEffect(() => {
    if (hasScrolled && tracking) {
      trackShowMoreTiles(tracking);
    }
  }, [hasScrolled, tracking]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, [properties]);

  return (
    <CarouselWrapper data-testid="property-carousel" className={className}>
      <Carousel
        ref={scrollRef}
        insideGap="0"
        dangerouslySetClassNames={{
          leftScrollButton: 'leftScrollButton',
          rightScrollButton: 'rightScrollButton',
          carouselList: 'carouselList',
        }}
        onScroll={() => setHasScrolled(true)}
      >
        {properties.map(
          (
            {
              path,
              clickThroughSource,
              image,
              badge,
              title,
              subtitle,
              features,
              titleExtra,
              target,
            },
            index,
          ) => {
            return (
              <CarouselTile
                path={path}
                clickThroughSource={clickThroughSource}
                image={image}
                badge={badge}
                title={title}
                titleExtra={titleExtra}
                subtitle={subtitle}
                features={features}
                key={path}
                preloadImage={index < preloadCount}
                target={target}
              />
            );
          },
        )}
      </Carousel>
    </CarouselWrapper>
  );
};
